import React, { forwardRef, useImperativeHandle, useReducer, useEffect, useContext, useState } from "react";
import { TextField, Text, DatePicker } from "@fluentui/react";
import { cx, css } from "@emotion/css";

import {
  invoiceDateStringError,
  postingDateStringError,
  translationDateStringError,
  referenceError,
  descriptionError,
  purposeError,
  companyCodeError,
  currencyCodeError,
  documentTypeError
} from "./utilities";
import { Autocomplete } from "../Autocomplete";
import { searchInArray } from "../../utilities/searchInArray";
import LoadingSpinner from "../../baseComponents/LoadingSpinner";
import { ISLCreateHeader } from "../../services/SLEntryFactory";
import { RequireAtLeastOne, ISLGetResults } from "../../services/TypeUtil";
import { RuntimeError } from "../../services/ErrorHelpers";
import { SubledgerContext } from "../../contexts/SubledgerContext";
import { trimNullableString } from "../../services/SanitizationHelpers";
import { formattedDateString } from "../../services/DateUtil";
import { ISLEntryResponse } from "./SLCreateSchemas";
import { SLLoadingStatus } from "../../utilities/utilities";
import { SLCreateSLBatchContext } from "../../contexts/SLCreateSLBatchContext";
import { isValid } from "date-fns";

const style = css`
  & .entry-header-text {
    margin: 0 0 8px 0;
  }
`;

const styles = {
  subComponentStyles: {
    label: {
      root: [
        {
          color: "var(--main-font-color, black)"
        }
      ]
    }
  }
};

export const SLCreateInitialState: ISLCreateHeader = {
  companyCode: "",
  reference: "",
  currencyCode: "",
  credit: "",
  debit: "",
  totalAmount: "",
  invoiceDate: "",
  translationDate: "",
  postingDate: "",
  documentType: "",
  purpose: "",
  description: "",
  rowVer: null
};

export interface ISLCreateHeaderGetter extends ISLGetResults {
  result: ISLCreateHeader;
}

export interface ISLCreateHeaderRef {
  getHeader: () => ISLCreateHeaderGetter;
  setHeader: (theHeader: ISLCreateHeader) => void;
  reset: () => void;
}

type headerReducer = (state: ISLCreateHeader, newState: RequireAtLeastOne<ISLCreateHeader>) => ISLCreateHeader;

enum ERRORS {
  COMPANY_CODE_ERROR,
  REFERENCE_ERROR,
  CURRENCY_CODE_ERROR,
  INVOICE_DATE_ERROR,
  TRANSLATION_DATE_ERROR,
  POSTING_DATE_ERROR,
  DOCUMENT_TYPE_ERROR,
  PURPOSE_ERROR,
  DESCRIPTION_ERROR
}

const initialErrorState = {
  companyCode: "",
  reference: "",
  currencyCode: "",
  invoiceDate: "",
  translationDate: "",
  postingDate: "",
  documentType: "",
  purpose: "",
  description: ""
};

function numberWithCommas(x: number) {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

function mapEntryToHeaderFields(entry: ISLEntryResponse): ISLCreateHeader {
  const headerData = {
    companyCode: trimNullableString(entry.companyCode),
    invoiceDate: formattedDateString(entry.invoiceDate, "MM/dd/yyyy"),
    postingDate: formattedDateString(entry.postingDate, "MM/dd/yyyy"),
    translationDate: formattedDateString(entry.translationDate, "MM/dd/yyyy"),
    reference: entry.reference,
    documentType: trimNullableString(entry.documentType),
    credit: entry.credit ? numberWithCommas(entry.credit) : "",
    debit: entry.debit ? numberWithCommas(entry.debit) : "",
    totalAmount: entry.amount ? numberWithCommas(entry.amount) : "",
    currencyCode: trimNullableString(entry.currency),
    description: entry.headerText,
    purpose: entry.purpose,
    rowVer: entry.rowVer
  };
  return headerData;
}

function sanitizeHeader(theHeader: ISLCreateHeader): ISLCreateHeader {
  // Remove all nulls from the API because they make life miserable
  const newHeader = Object.keys(SLCreateInitialState).reduce((ctr, k: string) => {
    const key = k as keyof typeof theHeader;
    if (theHeader[key] === null && key !== "rowVer") {
      ctr[key] = "";
    } else if (key.indexOf("Date") > -1) {
      if (isValid(new Date(theHeader[key]))) {
        ctr[key] = new Date(theHeader[key]);
      }
    } else {
      ctr[key] = theHeader[key];
    }
    return ctr;
  }, {} as typeof theHeader);
  return newHeader;
}

const SLCreateHeader = forwardRef((props, ref: React.Ref<ISLCreateHeaderRef>) => {
  const { companyCodes, currencyCodes, documentTypes } = useContext(SubledgerContext);
  const slCreateContext = useContext(SLCreateSLBatchContext);
  const [loadingState, setLoadingState] = useState(SLLoadingStatus.Idle);

  const [header, _setHeader] = useReducer<headerReducer>(
    (state, newState) => ({ ...state, ...newState }),
    SLCreateInitialState
  );

  const [errors, setErrors] = useReducer((state: typeof initialErrorState, type: ERRORS) => {
    const newState = { ...state };
    switch (type) {
      case ERRORS.COMPANY_CODE_ERROR:
        newState.companyCode = companyCodeError(companyCodes.value, header.companyCode);
        break;
      case ERRORS.REFERENCE_ERROR:
        newState.reference = referenceError(header.reference);
        break;
      case ERRORS.CURRENCY_CODE_ERROR:
        newState.currencyCode = currencyCodeError(currencyCodes.value, header.currencyCode);
        break;
      case ERRORS.INVOICE_DATE_ERROR:
        newState.invoiceDate = invoiceDateStringError(header.invoiceDate);
        break;
      case ERRORS.TRANSLATION_DATE_ERROR:
        newState.translationDate = translationDateStringError(header.translationDate, header.postingDate);
        break;
      case ERRORS.POSTING_DATE_ERROR:
        newState.postingDate = postingDateStringError(header.postingDate);
        break;
      case ERRORS.DOCUMENT_TYPE_ERROR:
        newState.documentType = documentTypeError(documentTypes.value, header.documentType);
        break;
      case ERRORS.PURPOSE_ERROR:
        newState.purpose = purposeError(header.purpose);
        break;
      case ERRORS.DESCRIPTION_ERROR:
        newState.description = descriptionError(header.description);
        break;
    }
    return newState;
  }, initialErrorState);

  useEffect(() => {
    if (slCreateContext.loadingStatus === SLLoadingStatus.Resolved) {
      if (slCreateContext.entry) {
        const entryFields = mapEntryToHeaderFields(slCreateContext.entry as ISLEntryResponse);
        const sanitizedHeader = sanitizeHeader(entryFields);
        _setHeader(sanitizedHeader);
      } else {
        _setHeader(SLCreateInitialState);
      }
      setLoadingState(SLLoadingStatus.Resolved);
    } else if (slCreateContext.loadingStatus === SLLoadingStatus.Rejected) {
      setLoadingState(SLLoadingStatus.Rejected);
    } else {
      setLoadingState(SLLoadingStatus.Pending);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slCreateContext.loadingStatus, slCreateContext.entry]);

  useEffect(() => {
    const noErrorsFound = Object.keys(errors).every((key: string) => errors[key as keyof typeof errors] === "");
    if (slCreateContext.loadingStatus === SLLoadingStatus.Resolved) {
      slCreateContext.setErrors({
        collectorName: "Header",
        value: !noErrorsFound
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors]);

  useEffect(() => {
    setErrors(ERRORS.COMPANY_CODE_ERROR);
  }, [header.companyCode]);
  useEffect(() => {
    setErrors(ERRORS.REFERENCE_ERROR);
  }, [header.reference]);
  useEffect(() => {
    setErrors(ERRORS.CURRENCY_CODE_ERROR);
  }, [header.currencyCode]);
  useEffect(() => {
    setErrors(ERRORS.INVOICE_DATE_ERROR);
  }, [header.invoiceDate]);
  useEffect(() => {
    setErrors(ERRORS.TRANSLATION_DATE_ERROR);
  }, [header.translationDate, header.postingDate]);
  useEffect(() => {
    setErrors(ERRORS.POSTING_DATE_ERROR);
  }, [header.postingDate]);
  useEffect(() => {
    setErrors(ERRORS.DOCUMENT_TYPE_ERROR);
  }, [header.documentType]);
  useEffect(() => {
    setErrors(ERRORS.PURPOSE_ERROR);
  }, [header.purpose]);
  useEffect(() => {
    setErrors(ERRORS.DESCRIPTION_ERROR);
  }, [header.description]);

  useImperativeHandle(
    ref,
    (): ISLCreateHeaderRef => ({
      getHeader() {
        const headerState = {
          result: {
            ...header,
            invoiceDate: header.invoiceDate ? new Date(header.invoiceDate) : "",
            postingDate: header.postingDate ? new Date(header.postingDate) : "",
            translationDate: header.translationDate ? new Date(header.translationDate) : ""
          },
          hasError: Object.values(errors).some((str) => str && !!str.trim())
        };
        return headerState;
      },
      setHeader(theHeader) {
        if (theHeader.rowVer === undefined) {
          throw new RuntimeError("rowVer undefined");
        }
        const sanitizedHeader = sanitizeHeader(theHeader);
        _setHeader(sanitizedHeader);
      },
      reset() {
        _setHeader(SLCreateInitialState);
      }
    })
  );

  const isInCompanyCodes = (prefix?: string) => searchInArray(companyCodes.value, prefix);
  const isInCurrencyCodes = (prefix?: string) => searchInArray(currencyCodes.value, prefix);
  const isInDocumentTypes = (prefix?: string) => searchInArray(documentTypes.value, prefix);

  if (loadingState === SLLoadingStatus.Pending) {
    return <LoadingSpinner label="Loading header" />;
  } else if (loadingState === SLLoadingStatus.Idle) {
    return null;
  } else if (loadingState === SLLoadingStatus.Rejected) {
    return (
      <Text
        variant="xLarge"
        style={{
          color: "var(--accent-font-color, gray)",
          textAlign: "center"
        }}
      >
        Error while loading SLEntry.
      </Text>
    );
  }

  return (
    <React.Fragment>
      <div className={cx("ms-Grid", style)} dir="ltr">
        <div className="ms-Grid-row">
          <div className="ms-Grid-col ms-lg3">
            <Autocomplete
              label="Company Code"
              name="companyCode"
              value={header.companyCode}
              className="entry-header-text"
              onChange={(value) => _setHeader({ companyCode: value || "" })}
              required
              errorMessage={errors.companyCode}
              onSuggest={isInCompanyCodes}
              disabled={slCreateContext.isReadOnly}
            />
          </div>
          <div className="ms-Grid-col ms-lg3">
            <TextField
              name="reference"
              label="Reference"
              value={header.reference}
              className="entry-header-text"
              onChange={(_, newValue) => {
                _setHeader({ reference: newValue || "" });
              }}
              required
              disabled={slCreateContext.isReadOnly}
              errorMessage={errors.reference}
            />
          </div>
          <div className="ms-Grid-col ms-lg3">
            <Autocomplete
              label="Currency Code"
              name="currencyCode"
              value={header.currencyCode}
              className="entry-header-text"
              onChange={(newValue) => _setHeader({ currencyCode: newValue || "" })}
              required
              disabled={slCreateContext.isReadOnly}
              errorMessage={errors.currencyCode}
              onSuggest={isInCurrencyCodes}
            />
          </div>

          <div className="ms-Grid-col ms-lg1">
            <TextField
              name="debit"
              label="Debit"
              value={header.debit}
              className="entry-header-text"
              onChange={(_, newValue) => _setHeader({ debit: newValue || "" })}
              required={false}
              disabled
              styles={styles}
            />
          </div>
          <div className="ms-Grid-col ms-lg1">
            <TextField
              name="credit"
              label="Credit"
              value={header.credit}
              className="entry-header-text"
              onChange={(_, newValue) => _setHeader({ credit: newValue || "" })}
              required={false}
              disabled
              styles={styles}
            />
          </div>
          <div className="ms-Grid-col ms-lg1">
            <TextField
              name="totalAmount"
              label="Total Amount"
              value={header.totalAmount}
              className="entry-header-text"
              onChange={(_, newValue) => _setHeader({ totalAmount: newValue || "" })}
              required={false}
              disabled
              styles={styles}
            />
          </div>
        </div>
        <div className="ms-Grid-row">
          <div className="ms-Grid-col ms-lg3">
            <DatePicker
              label={"Invoice Date"}
              ariaLabel={"Invoice date selection"}
              isRequired
              disabled={slCreateContext.isReadOnly}
              value={header.invoiceDate}
              onSelectDate={(newValue) => _setHeader({ invoiceDate: newValue || "" })}
              // errorMessage={errors.invoiceDate}
              textField={{ errorMessage: errors.invoiceDate }}
            />
          </div>
          <div className="ms-Grid-col ms-lg3">
            <DatePicker
              label={"Translation Date"}
              isRequired
              disabled={slCreateContext.isReadOnly}
              value={header.translationDate}
              ariaLabel={"Translation date selection"}
              onSelectDate={(newValue) => _setHeader({ translationDate: newValue || "" })}
              // errorMessage={errors.translationDate}
              textField={{ errorMessage: errors.translationDate }}
            />
          </div>
          <div className="ms-Grid-col ms-lg3">
            <DatePicker
              label={"Posting Date"}
              isRequired
              disabled={slCreateContext.isReadOnly}
              value={header.postingDate}
              ariaLabel={"Posting date selection"}
              onSelectDate={(newValue) => _setHeader({ postingDate: newValue || "" })}
              // errorMessage={errors.postingDate}
              textField={{ errorMessage: errors.postingDate }}
            />
          </div>
          <div className="ms-Grid-col ms-lg3">
            <Autocomplete
              label="Document Type"
              name="documentType"
              value={header.documentType}
              className="entry-header-text"
              onChange={(newValue) => _setHeader({ documentType: newValue || "" })}
              required
              disabled={slCreateContext.isReadOnly}
              errorMessage={errors.documentType}
              onSuggest={isInDocumentTypes}
            />
          </div>
        </div>
        <div className="ms-Grid-row">
          <div className="ms-Grid-col ms-lg6">
            <TextField
              name="purpose"
              label="Purpose"
              value={header.purpose}
              className="entry-header-text"
              onChange={(_, newValue) => _setHeader({ purpose: newValue || "" })}
              required={true}
              disabled={slCreateContext.isReadOnly}
              errorMessage={errors.purpose}
              multiline={true}
            />
          </div>
          <div className="ms-Grid-col ms-lg6">
            <TextField
              name="description"
              label="Description"
              value={header.description}
              onChange={(_, newValue) => _setHeader({ description: newValue || "" })}
              className="entry-header-text"
              errorMessage={errors.description}
              required={true}
              disabled={slCreateContext.isReadOnly}
              autoAdjustHeight={true}
              multiline={true}
            />
          </div>
        </div>
      </div>
    </React.Fragment>
  );
});

SLCreateHeader.displayName = "SLCreateHeader";

export default SLCreateHeader;
